/**
 * Product hotspot reveal animation.
 *
 * Targets the primary hotspot set on the first product media, excluding the `--last`
 * duplicate on the trailing slide. On load they expand from their anchor dot, hold ~3s,
 * then collapse.
 *
 * Re-reveal differs by input: desktop uses hover (click is taken by the zoom dialog) and
 * collapses on leave; touch re-expands on any tap in the media area outside a hotspot or
 * the zoom button, staying open until a slide change.
 *
 * State is one `hotspots-expanded` class on the gallery so hotspots animate in sync. Nothing
 * is preventDefault'd — reveal layers passively over existing zoom/navigation.
 */

import { SlideshowSelectEvent } from '@theme/events';

const GALLERY_SELECTOR = 'media-gallery';
const MEDIA_CONTAINER_SELECTOR = '.product-media-container';
const HOTSPOT_SELECTOR = '.product-media-container--hotspot';
const ANIMATABLE_HOTSPOT_SELECTOR =
  '.product-media-container--hotspot:not(.product-media-container--hotspot--last)';
const ZOOM_BUTTON_SELECTOR = '.product-media-container__zoom-button';
const EXPANDED_CLASS = 'hotspots-expanded';
const HOLD_DURATION_MS = 3000;

/** Galleries the user has permanently re-expanded — never auto-collapse these again. */
const lockedGalleries = new WeakSet();

/** Galleries the cursor is currently over (desktop) — kept expanded until the pointer leaves. */
const hoveredGalleries = new WeakSet();

/**
 * @param {Element} gallery
 * @returns {boolean}
 */
function hasAnimatableHotspots(gallery) {
  return gallery.querySelector(ANIMATABLE_HOTSPOT_SELECTOR) != null;
}

/** @returns {boolean} */
function prefersReducedMotion() {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** @returns {boolean} True on devices whose primary input can hover (e.g. desktop mouse). */
function canHover() {
  return typeof matchMedia === 'function' && matchMedia('(hover: hover)').matches;
}

/**
 * Entrance: expand outward from the dot, hold, then collapse back (unless locked).
 * @param {Element} gallery
 */
function playEntrance(gallery) {
  if (!hasAnimatableHotspots(gallery)) return;

  // Reduced motion: reveal the hotspots and leave them expanded, skipping the timed
  // collapse so they don't flash in then disappear.
  if (prefersReducedMotion()) {
    lockedGalleries.add(gallery);
    gallery.classList.add(EXPANDED_CLASS);
    return;
  }

  // Compact is the initial CSS state; defer the expand so the transition runs.
  requestAnimationFrame(() => {
    gallery.classList.add(EXPANDED_CLASS);
    setTimeout(() => {
      // Keep them expanded if the user has locked them (touch) or is hovering (desktop),
      // so the timed collapse never pulls them out from under an active cursor.
      if (lockedGalleries.has(gallery) || hoveredGalleries.has(gallery)) return;
      gallery.classList.remove(EXPANDED_CLASS);
    }, HOLD_DURATION_MS);
  });
}

/**
 * @param {Event} event
 */
function handleClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  // Only the image tiles count as "the PDP image area".
  if (!target.closest(MEDIA_CONTAINER_SELECTOR)) return;

  // Don't hijack a hotspot's own drawer trigger or the zoom control.
  if (target.closest(HOTSPOT_SELECTOR) || target.closest(ZOOM_BUTTON_SELECTOR)) return;

  const gallery = target.closest(GALLERY_SELECTOR);
  if (!(gallery instanceof Element) || !hasAnimatableHotspots(gallery)) return;

  lockedGalleries.add(gallery);
  gallery.classList.add(EXPANDED_CLASS);
}

/**
 * The media tile under `target` that actually carries animatable hotspots (the first
 * media), or null. Scopes the hover reveal to that one image so hovering other gallery
 * tiles or thumbnails doesn't trigger it.
 * @param {Element | null} target
 * @returns {Element | null}
 */
function hotspotContainerFrom(target) {
  const container = target ? target.closest(MEDIA_CONTAINER_SELECTOR) : null;
  if (!(container instanceof Element)) return null;
  return container.querySelector(ANIMATABLE_HOTSPOT_SELECTOR) ? container : null;
}

/**
 * Desktop hover-in: reveal the hotspots while the cursor is over the image that carries them.
 * @param {Event} event
 */
function handleMouseOver(event) {
  const target = event.target instanceof Element ? event.target : null;
  const container = hotspotContainerFrom(target);
  if (!container) return;

  const gallery = container.closest(GALLERY_SELECTOR);
  if (!(gallery instanceof Element) || hoveredGalleries.has(gallery)) return;

  hoveredGalleries.add(gallery);
  gallery.classList.add(EXPANDED_CLASS);
}

/**
 * Desktop hover-out: collapse once the cursor truly leaves the hotspot image. mouseout also
 * fires when moving between descendants, so ignore moves that stay within that tile.
 * @param {Event} event
 */
function handleMouseOut(event) {
  const target = event.target instanceof Element ? event.target : null;
  const container = hotspotContainerFrom(target);
  if (!container) return;

  const gallery = container.closest(GALLERY_SELECTOR);
  if (!(gallery instanceof Element) || !hoveredGalleries.has(gallery)) return;

  // Moving to another element still inside the hotspot tile is not a real leave.
  const related = event.relatedTarget instanceof Element ? event.relatedTarget : null;
  if (related && container.contains(related)) return;

  hoveredGalleries.delete(gallery);
  if (!lockedGalleries.has(gallery)) gallery.classList.remove(EXPANDED_CLASS);
}

/**
 * Disengage on slide change: collapse the hotspots (and drop the touch lock) when the
 * user navigates to another slide, so they return to compact and a fresh tap is needed
 * to reveal them again. Guarded on `userInitiated` so the programmatic initial slide
 * selection doesn't cut the entrance animation short.
 * @param {SlideshowSelectEvent} event
 */
function handleSlideshowSelect(event) {
  if (!event.detail?.userInitiated) return;

  const target = event.target instanceof Element ? event.target : null;
  const gallery = target?.closest(GALLERY_SELECTOR);
  if (!(gallery instanceof Element) || !hasAnimatableHotspots(gallery)) return;

  lockedGalleries.delete(gallery);
  hoveredGalleries.delete(gallery);
  gallery.classList.remove(EXPANDED_CLASS);
}

function init() {
  document.querySelectorAll(GALLERY_SELECTOR).forEach((gallery) => playEntrance(gallery));
}

// Guard against double-binding if the module is re-evaluated.
if (!window.__hotspotAnimationWired) {
  window.__hotspotAnimationWired = true;

  // Desktop reveals on hover (click is reserved for the zoom dialog); touch falls back to tap.
  if (canHover()) {
    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);
  } else {
    document.addEventListener('click', handleClick);
  }

  // Switching slides collapses the (touch-locked) hotspots back to compact.
  document.addEventListener(SlideshowSelectEvent.eventName, handleSlideshowSelect);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
